import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import studioApplicationService from '../../utils/studioApplicationService';

const StudioApplicationForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const autoSubmit = searchParams.get('autoSubmit') === 'true';
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    studioName: '',
    studioWebsite: '',
    studioDescription: '',
    teamSize: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!user) {
      navigate('/user-login?returnTo=/studio-application-form');
      return;
    }

    // Check for pending application data from registration
    const pendingData = sessionStorage.getItem('pendingStudioApplication');
    if (pendingData) {
      try {
        const data = JSON.parse(pendingData);
        setFormData(data);
        sessionStorage.removeItem('pendingStudioApplication');

        // If autoSubmit flag is set, submit the form after a short delay
        if (autoSubmit) {
          setTimeout(() => {
            submitApplication(data);
          }, 500);
        }
      } catch (err) {
        console.error('Error parsing pending application data:', err);
      }
    } else {
      // Check if user already has an application
      const checkExistingApplication = async () => {
        const result = await studioApplicationService.getMyApplication();
        if (result.success && result.data) {
          // Already has an application, redirect to pending page
          navigate('/studio-application-pending');
        }
      };

      checkExistingApplication();
    }
  }, [user, navigate, autoSubmit]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.studioName || formData.studioName.trim() === '') {
      newErrors.studioName = 'Studio name is required';
    }

    if (formData.studioWebsite && !/^https?:\/\/.+\..+/.test(formData.studioWebsite)) {
      newErrors.studioWebsite = 'Please enter a valid URL';
    }

    if (!formData.studioDescription || formData.studioDescription.trim() === '') {
      newErrors.studioDescription = 'Studio description is required';
    } else if (formData.studioDescription.length < 50) {
      newErrors.studioDescription = 'Please provide at least 50 characters';
    }

    if (!formData.teamSize) {
      newErrors.teamSize = 'Team size is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitApplication = async (data = formData) => {
    setLoading(true);

    try {
      console.log('📝 Submitting studio application with data:', data);
      const result = await studioApplicationService.submitApplication(data);
      console.log('📝 Studio application result:', result);

      if (result.success) {
        console.log('✅ Application submitted successfully, redirecting...');
        navigate('/studio-application-pending');
      } else {
        console.error('❌ Application submission failed:', result.error);
        setErrors({ submit: result.error });
        setLoading(false); // Make sure to stop loading on error
      }
    } catch (err) {
      console.error('❌ Exception during application submission:', err);
      setErrors({ submit: 'Failed to submit application. Please try again.' });
      setLoading(false); // Make sure to stop loading on error
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    await submitApplication();
  };

  // Show loading state during auto-submit
  if (autoSubmit && loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          <div className="bg-card border border-border rounded-2xl shadow-gaming-xl p-12 text-center">
            <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Loader2" size={32} className="text-blue-500 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Submitting Your Application
            </h2>
            <p className="text-muted-foreground">
              Please wait while we process your studio application...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="bg-card border border-border rounded-2xl shadow-gaming-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Gamepad2" size={32} className="text-blue-500" />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Apply for Studio Access
            </h1>
            <p className="text-muted-foreground">
              Tell us about your studio and start publishing games on Solis Games
            </p>
          </div>

          {/* Error Display */}
          {errors.submit && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" size={20} className="text-destructive" />
                <span className="text-sm text-destructive">{errors.submit}</span>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <Input
                label="Studio Name"
                type="text"
                placeholder="Enter your studio name"
                value={formData.studioName}
                onChange={(e) => handleInputChange('studioName', e.target.value)}
                error={errors.studioName}
                required
                disabled={loading}
              />

              <Input
                label="Studio Website"
                type="url"
                placeholder="https://yourstudio.com"
                value={formData.studioWebsite}
                onChange={(e) => handleInputChange('studioWebsite', e.target.value)}
                error={errors.studioWebsite}
                description="Optional - Your studio's official website"
                disabled={loading}
              />

              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Studio Description <span className="text-destructive">*</span>
                </label>
                <textarea
                  placeholder="Tell us about your studio and the games you develop... (minimum 50 characters)"
                  value={formData.studioDescription}
                  onChange={(e) => handleInputChange('studioDescription', e.target.value)}
                  className="flex min-h-[120px] w-full rounded-md border border-input bg-white text-black px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={loading}
                  rows={5}
                />
                <div className="flex justify-between items-center mt-1">
                  {errors.studioDescription && (
                    <p className="text-sm text-destructive">{errors.studioDescription}</p>
                  )}
                  <p className="text-xs text-muted-foreground ml-auto">
                    {formData.studioDescription.length} / 50 characters minimum
                  </p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Team Size <span className="text-destructive">*</span>
                </label>
                <select
                  value={formData.teamSize}
                  onChange={(e) => handleInputChange('teamSize', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-white text-black px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={loading}
                >
                  <option value="">Select team size</option>
                  <option value="solo">Solo Developer</option>
                  <option value="2-5">2-5 people</option>
                  <option value="6-10">6-10 people</option>
                  <option value="11-50">11-50 people</option>
                  <option value="51+">51+ people</option>
                </select>
                {errors.teamSize && (
                  <p className="text-sm text-destructive mt-1">{errors.teamSize}</p>
                )}
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex gap-3">
                <Icon name="Info" size={20} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="space-y-2 text-sm">
                  <p className="text-blue-900 dark:text-blue-100 font-medium">What happens next?</p>
                  <ul className="text-blue-800 dark:text-blue-200 space-y-1 list-disc list-inside">
                    <li>Your application will be reviewed by our team</li>
                    <li>We typically respond within 2-3 business days</li>
                    <li>You'll receive an email once your application is reviewed</li>
                    <li>Once approved, you can start publishing games immediately</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="button"
                variant="outline"
                size="lg"
                fullWidth
                onClick={() => navigate('/game-library-dashboard')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                size="lg"
                fullWidth
                loading={loading}
                disabled={loading}
                iconName="Send"
              >
                Submit Application
              </Button>
            </div>
          </form>

          {/* Help text */}
          <p className="text-xs text-muted-foreground text-center mt-6">
            Questions about the studio program? <a href="/contact-us" className="text-primary hover:underline">Contact us</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default StudioApplicationForm;
